// A small ring buffer of what went wrong in this tab recently, for Help & feedback reports
// (docs/help-feedback-plan.md). Two feeds: uncaught errors / rejections / console.error, and
// failed API calls (hooked from the axios response interceptor in utils/http.js).
//
// What is kept is deliberately thin — a message, a URL path, a status — never request bodies,
// headers or tokens; the server redacts again (utils/support/reportContext.js) but nothing
// sensitive should get that far.
const MAX = 20;
const clientErrors = [];
const failedRequests = [];
let installed = false;

// Kept in sessionStorage (this tab only, cleared when it closes) so the errors that made a
// user reload the page are still in the report they send afterwards. Best effort: storage can
// be unavailable (private mode, quota) and the buffer must work without it.
const STORAGE_KEY = "cdt:errorBuffer";
const persist = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ clientErrors, failedRequests }));
  } catch {
    /* no storage: memory only */
  }
};
const restore = () => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "null");
    if (Array.isArray(saved?.clientErrors)) clientErrors.push(...saved.clientErrors.slice(-MAX));
    if (Array.isArray(saved?.failedRequests)) failedRequests.push(...saved.failedRequests.slice(-MAX));
  } catch {
    /* ignore a corrupt or missing entry */
  }
};

const now = () => new Date().toISOString();
const push = (list, item) => {
  list.push(item);
  if (list.length > MAX) list.splice(0, list.length - MAX);
  persist();
};
const short = (v, n = 600) => {
  const s = typeof v === "string" ? v : (() => { try { return JSON.stringify(v); } catch { return String(v); } })();
  return s && s.length > n ? `${s.slice(0, n)}…` : s || "";
};
const pathOf = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return `${u.pathname}${u.search ? u.search.slice(0, 200) : ""}`;
  } catch {
    return short(url, 300);
  }
};

export const recordClientError = ({ message, source = "", stack = "" }) =>
  push(clientErrors, { at: now(), message: short(message, 800), source: short(source, 200), stack: short(stack, 1200) });

export const recordFailedRequest = ({ method, url, status, message }) =>
  push(failedRequests, { at: now(), method: String(method || "GET").toUpperCase(), url: pathOf(url), status: status ?? null, message: short(message, 400) });

export const getErrorBuffer = () => ({ clientErrors: [...clientErrors], failedRequests: [...failedRequests] });

export const clearErrorBuffer = () => {
  clientErrors.length = 0;
  failedRequests.length = 0;
  persist();
};

// Installed once at app start (index.js). console.error is wrapped, not replaced: React and
// libraries log real problems there that never reach window.onerror.
export const installErrorBuffer = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;
  restore();
  window.addEventListener("error", (event) => {
    const err = event?.error;
    recordClientError({
      message: err?.message || event?.message || "Unknown error",
      source: event?.filename ? `${event.filename}:${event.lineno || 0}` : "",
      stack: err?.stack || "",
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    recordClientError({
      message: reason?.message || short(reason, 300) || "Unhandled rejection",
      source: "unhandledrejection",
      stack: reason?.stack || "",
    });
  });
  const original = console.error;
  console.error = (...args) => {
    try {
      recordClientError({
        message: args.map((a) => (a instanceof Error ? a.message : short(a, 300))).join(" "),
        source: "console.error",
        stack: args.find((a) => a instanceof Error)?.stack || "",
      });
    } catch {
      /* never let the buffer break logging */
    }
    original.apply(console, args);
  };
};
